import type { FeatureFlags } from '@magicschool/business-logic';
import type { NavLinkProps } from '@magicschool/spellbook';
import type { InstitutionRole, MagicSchoolUser } from '@magicschool/supabase/user';
import { hasAnyInstitutionRole } from '@magicschool/supabase/user/utils';
import type { UserRole } from '@/features/server/actions/rbac/types';
import { isAtLeastRole } from '@/util/UserRole';

export type NavItem = {
  altUrl?: string;
  animate?: NavLinkProps['animate'];
  children?: NavItem[];
  dataTestId?: string;
  exactMatch?: boolean;
  matchPathPrefixes?: string[];
  intlId?: string;
  icon?: string;
  id: string;
  isNew?: boolean;
  badgeIntlId?: string;
  requireAdminAccess?: boolean;
  requireAtLeastRole?: UserRole;
  requireInstitutionRole?: InstitutionRole;
  requireMagicStudent?: boolean;
  requireExperimentFlag?: keyof FeatureFlags;
  url?: string;
};

export const getLegacyEducatorNavItems = (): NavItem[] => [
  {
    id: 'tools',
    dataTestId: 'teacher-tools-dropdown',
    intlId: 'magic-tools',
    icon: 'wand-sparkles',
    children: [
      {
        id: 'tools',
        dataTestId: 'nav-teacher-tools',
        intlId: 'magic-tools.teacher',
        url: '/tools',
      },
      {
        id: 'bulk-writing-feedback',
        dataTestId: 'nav-class-writing-feedback',
        intlId: 'bulk-writing-feedback',
        url: '/reviewers',
      },
      {
        id: 'collections',
        dataTestId: 'nav-my-collections',
        intlId: 'collections',
        url: '/collections',
      },
      {
        id: 'history',
        dataTestId: 'nav-output-history',
        intlId: 'chat-history',
        url: '/history',
      },
      {
        id: 'resources',
        dataTestId: 'nav-resource-library',
        intlId: 'resource-library',
        url: '/resources',
      },
    ],
  },
  {
    id: 'raina',
    intlId: 'raina-ai-chatbot',
    url: '/raina',
    icon: 'message-square-text',
  },
  {
    id: 'classes',
    intlId: 'navigation.classes',
    url: '/classes',
    icon: 'users-round',
    requireAtLeastRole: 'teacher',
    isNew: true,
    badgeIntlId: 'badge-beta',
  },
  {
    id: 'magic-student',
    intlId: 'magic-student',
    dataTestId: 'magic-student-dropdown',
    icon: 'graduation-cap',
    requireMagicStudent: true,
    children: [
      {
        id: 'magic-student-tools',
        intlId: 'magic-student.teacher-header.tools',
        url: '/magic-student/tools',
        altUrl: '/magic-student/room/new',
        requireMagicStudent: true,
      },
      {
        id: 'magic-student-rooms',
        intlId: 'magic-student.teacher-header.student-rooms',
        url: '/magic-student/rooms',
        requireMagicStudent: true,
      },
      {
        id: 'magic-student-resources',
        intlId: 'magic-student.teacher-header.resources',
        url: '/magic-student/resources',
        requireMagicStudent: true,
      },
      {
        id: 'magic-student-quizzes',
        intlId: 'magic-student.teacher-header.quizzes',
        url: '/quizzes',
        requireMagicStudent: true,
        isNew: true,
        badgeIntlId: 'badge-beta',
      },
    ],
  },
  {
    id: 'org-admin',
    intlId: 'navigation.org-home',
    icon: 'settings',
    requireInstitutionRole: 'admin',
    requireAtLeastRole: 'org_admin',
    children: [
      {
        id: 'org-users',
        intlId: 'navigation.org-users',
        url: '/o/users',
        requireInstitutionRole: 'admin',
        requireAtLeastRole: 'org_admin',
      },
      {
        id: 'org-dashboard',
        intlId: 'navigation.org-reporting-dashboard',
        url: '/o/dashboard',
        requireInstitutionRole: 'admin',
        requireAtLeastRole: 'org_admin',
      },
      {
        id: 'org-rooms',
        intlId: 'org.rooms.header',
        url: '/o/rooms',
        requireInstitutionRole: 'admin',
        requireAtLeastRole: 'org_admin',
        requireMagicStudent: true,
      },
      {
        id: 'org-tools',
        intlId: 'navigation.org-tools',
        url: '/o/tools',
        requireInstitutionRole: 'admin',
        requireAtLeastRole: 'org_admin',
      },
      {
        id: 'org-moderations',
        intlId: 'navigation.org-moderations',
        url: '/o/moderations',
        requireAtLeastRole: 'org_admin',
      },
      {
        id: 'org-settings',
        intlId: 'navigation.org-settings',
        url: '/o/settings',
        requireAtLeastRole: 'org_admin',
      },
      {
        id: 'org-knowledge-base',
        intlId: 'navigation.org-knowledge-base',
        url: '/o/knowledge-base',
        requireAtLeastRole: 'org_admin',
        isNew: true,
        badgeIntlId: 'badge-beta',
      },
    ],
  },
  {
    id: 'magic-admin',
    intlId: 'admin',
    dataTestId: 'admin-nav-dropdown',
    icon: 'crown',
    requireAtLeastRole: 'internal',
    children: [
      {
        id: 'admin-user-search',
        intlId: 'navigation.admin-user-search',
        url: '/admin/users',
        dataTestId: 'admin-user-search',
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-org-search',
        intlId: 'navigation.admin-org-search',
        url: '/admin/orgs',
        dataTestId: 'admin-org-search',
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-demo-orgs',
        intlId: 'navigation.admin-demo-orgs',
        url: '/admin/demo-orgs',
        dataTestId: 'admin-demo-orgs',
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-enterprise-tools-manager',
        intlId: 'navigation.admin-enterprise-tools-manager',
        url: '/admin/enterprise-tools-manager',
        dataTestId: 'admin-enterprise-tools-manager',
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-tools',
        intlId: 'navigation.admin-tools',
        url: '/admin/tools',
        requireAdminAccess: true,
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-tool-languages',
        intlId: 'navigation.admin-translations',
        url: '/admin/translations',
        requireAdminAccess: true,
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-system-configuration',
        intlId: 'navigation.admin-system-messages',
        url: '/admin/system',
        dataTestId: 'system-messages',
        requireAdminAccess: true,
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-standards-management',
        intlId: 'navigation.admin-standards',
        url: '/admin/standards',
        dataTestId: 'admin-standards-management',
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-migrate-user',
        intlId: 'navigation.migrate-user',
        url: '/admin/migrate-user',
        dataTestId: 'admin-migrate-user',
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-feedback',
        intlId: 'navigation.admin-feedback',
        url: '/admin/feedback',
        dataTestId: 'admin-feedback',
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-image-generation',
        intlId: 'navigation.admin-image-generation',
        url: '/admin/image-generation',
        dataTestId: 'admin-image-generation',
        requireAtLeastRole: 'superuser',
      },
      {
        id: 'admin-presentations',
        intlId: 'navigation.admin-presentations',
        url: '/admin/presentations',
        // The section root redirects to /decks, and `isNavItemCurrent` matches exactly, so without
        // the altUrl the entry never reads as current on the page it actually lands you on.
        altUrl: '/admin/presentations/decks',
        dataTestId: 'admin-presentations',
        // The (admin-access) layout redirects to / when adminAccess is off, and the internal role
        // does not imply that flag, so without this the entry is a dead link for any internal user
        // lacking the ADMIN_ACCESS attribute.
        requireAdminAccess: true,
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-maintenance-banner',
        intlId: 'navigation.admin-maintenance-banner',
        url: '/admin/maintenance-banner',
        dataTestId: 'admin-maintenance-banner',
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-blocked-users',
        intlId: 'navigation.admin-blocked-users',
        url: '/admin/blocked-users',
        dataTestId: 'admin-blocked-users',
        requireAdminAccess: true,
        requireAtLeastRole: 'internal',
      },
      {
        id: 'admin-writing-feedback-runs',
        intlId: 'navigation.admin-writing-feedback-runs',
        url: '/admin/writing-feedback-runs',
        dataTestId: 'admin-writing-feedback-runs',
        requireAdminAccess: true,
        requireAtLeastRole: 'internal',
      },
    ],
  },
];

export const getEducatorNavItems = (): NavItem[][] => [
  [
    {
      id: 'home',
      dataTestId: 'nav-home',
      intlId: 'navigation.home',
      url: '/',
      icon: 'message-circle-plus',
      animate: 'hover',
      exactMatch: true,
    },
    {
      id: 'tools',
      dataTestId: 'nav-tools',
      intlId: 'navigation.all-tools',
      url: '/tools',
      icon: 'wrench',
      animate: 'hover',
    },
    {
      id: 'history',
      dataTestId: 'nav-history',
      intlId: 'chat-history',
      url: '/history',
      icon: 'history',
      animate: 'hover',
    },
    {
      id: 'resources',
      dataTestId: 'nav-resources',
      intlId: 'navigation.resources',
      url: '/resources',
      icon: 'bookmark',
      animate: 'hover',
    },
    {
      id: 'classes',
      dataTestId: 'nav-classes',
      intlId: 'navigation.classes',
      url: '/classes',
      icon: 'users',
      animate: 'hover',
      isNew: true,
      badgeIntlId: 'badge-beta',
    },
  ],
  [
    {
      id: 'magic-student',
      intlId: 'magic-student',
      dataTestId: 'magic-student-dropdown',
      icon: 'graduation-cap',
      animate: 'hover',
      requireMagicStudent: true,
      children: [
        {
          id: 'magic-student-rooms',
          intlId: 'magic-student.teacher-header.student-rooms',
          url: '/magic-student/rooms',
          requireMagicStudent: true,
        },
        {
          id: 'magic-student-resources',
          intlId: 'magic-student.teacher-header.room-templates',
          url: '/magic-student/resources',
          requireMagicStudent: true,
        },
        {
          id: 'magic-student-quizzes',
          intlId: 'magic-student.teacher-header.quizzes',
          url: '/quizzes',
          requireMagicStudent: true,
          isNew: true,
          badgeIntlId: 'badge-beta',
        },
      ],
    },
    {
      id: 'workflows',
      dataTestId: 'nav-workflows',
      intlId: 'navigation.workflows',
      icon: 'route',
      animate: 'hover',
      url: '/workflows',
      isNew: true,
      badgeIntlId: 'badge-beta',
    },
  ],
  [
    {
      id: 'org-admin',
      intlId: 'navigation.org-home',
      icon: 'layers',
      animate: 'hover',
      requireInstitutionRole: 'admin',
      requireAtLeastRole: 'org_admin',
      children: [
        {
          id: 'org-users',
          intlId: 'navigation.org-users',
          url: '/o/users',
          requireInstitutionRole: 'admin',
          requireAtLeastRole: 'org_admin',
        },
        {
          id: 'org-dashboard',
          intlId: 'navigation.org-reporting-dashboard',
          url: '/o/dashboard',
          requireInstitutionRole: 'admin',
          requireAtLeastRole: 'org_admin',
        },
        {
          id: 'org-rooms',
          intlId: 'org.rooms.header',
          url: '/o/rooms',
          requireInstitutionRole: 'admin',
          requireAtLeastRole: 'org_admin',
          requireMagicStudent: true,
        },
        {
          id: 'org-tools',
          intlId: 'navigation.org-tools',
          url: '/o/tools',
          requireInstitutionRole: 'admin',
          requireAtLeastRole: 'org_admin',
        },
        {
          id: 'org-moderations',
          intlId: 'navigation.org-moderations',
          url: '/o/moderations',
          requireAtLeastRole: 'org_admin',
        },
        {
          id: 'org-settings',
          intlId: 'navigation.org-settings',
          url: '/o/settings',
          requireAtLeastRole: 'org_admin',
        },
        {
          id: 'org-knowledge-base',
          intlId: 'navigation.org-knowledge-base',
          url: '/o/knowledge-base',
          requireAtLeastRole: 'org_admin',
          isNew: true,
          badgeIntlId: 'badge-beta',
        },
      ],
    },
    {
      id: 'magic-admin',
      intlId: 'admin',
      dataTestId: 'admin-nav-dropdown',
      icon: 'crown',
      requireAtLeastRole: 'internal',
      children: [
        {
          id: 'admin-user-search',
          intlId: 'navigation.admin-user-search',
          url: '/admin/users',
          dataTestId: 'admin-user-search',
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-org-search',
          intlId: 'navigation.admin-org-search',
          url: '/admin/orgs',
          dataTestId: 'admin-org-search',
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-demo-orgs',
          intlId: 'navigation.admin-demo-orgs',
          url: '/admin/demo-orgs',
          dataTestId: 'admin-demo-orgs',
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-enterprise-tools-manager',
          intlId: 'navigation.admin-enterprise-tools-manager',
          url: '/admin/enterprise-tools-manager',
          dataTestId: 'admin-enterprise-tools-manager',
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-tools',
          intlId: 'navigation.admin-tools',
          url: '/admin/tools',
          requireAdminAccess: true,
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-tool-languages',
          intlId: 'navigation.admin-translations',
          url: '/admin/translations',
          requireAdminAccess: true,
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-system-configuration',
          intlId: 'navigation.admin-system-messages',
          url: '/admin/system',
          dataTestId: 'system-messages',
          requireAdminAccess: true,
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-standards-management',
          intlId: 'navigation.admin-standards',
          url: '/admin/standards',
          dataTestId: 'admin-standards-management',
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-migrate-user',
          intlId: 'navigation.migrate-user',
          url: '/admin/migrate-user',
          dataTestId: 'admin-migrate-user',
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-feedback',
          intlId: 'navigation.admin-feedback',
          url: '/admin/feedback',
          dataTestId: 'admin-feedback',
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-image-generation',
          intlId: 'navigation.admin-image-generation',
          url: '/admin/image-generation',
          dataTestId: 'admin-image-generation',
          requireAtLeastRole: 'superuser',
        },
        {
          id: 'admin-presentations',
          intlId: 'navigation.admin-presentations',
          url: '/admin/presentations',
          altUrl: '/admin/presentations/decks',
          dataTestId: 'admin-presentations',
          requireAdminAccess: true,
          requireAtLeastRole: 'internal',
        },
        {
          id: 'admin-maintenance-banner',
          intlId: 'navigation.admin-maintenance-banner',
          url: '/admin/maintenance-banner',
          dataTestId: 'admin-maintenance-banner',
          requireAtLeastRole: 'internal',
        },
      ],
    },
  ],
];

export const getStudentNavItems = (roomId: string, isLTI: boolean): NavItem[] => {
  const basePathname = `${isLTI ? '/lti' : ''}/s/rooms/${roomId}`;

  return [
    {
      icon: 'wand-sparkles',
      id: 'tools',
      intlId: 'magic-tools',
      url: `${basePathname}/tools`,
    },
    {
      icon: 'list-check',
      id: 'history',
      intlId: 'output-history',
      url: `${basePathname}/history`,
    },
  ];
};

export function filterNavItems(items: NavItem[], predicates: ((item: NavItem) => boolean)[]): NavItem[] {
  return items.filter((item) => {
    const isItemValid = predicates.every((predicate) => predicate(item));
    if (item.children && item.children.length > 0) {
      item.children = filterNavItems(item.children, predicates);
    }
    return isItemValid || (item.children && item.children.length > 0);
  });
}

/**
 * Determines if a user can access a specific navigation item based on role requirements.
 *
 * @param item - The navigation item to check access for
 * @param user - The current user, or null if no user is logged in
 * @returns {boolean} - Whether the user has access to the navigation item
 *
 * @description
 * This function checks access to a navigation item based on the following rules:
 * 1. If no role requirements are set, the item is always accessible
 * 2. If no user is logged in, the item is not accessible
 * 3. If an institution role is required:
 *    - The user must have an institution context
 *    - The user must have the specified institution role
 * 4. If a minimum user role is required:
 *    - The user's role must meet or exceed the specified role
 */
function canUserRoleAccessItem(item: NavItem, user: MagicSchoolUser | null): boolean {
  // If no role restrictions, the item is always accessible
  if (!item.requireInstitutionRole && !item.requireAtLeastRole) {
    return true;
  }

  // If no user is logged in, deny access
  if (!user) {
    return false;
  }

  // Check institution-specific role requirement
  if (item.requireInstitutionRole) {
    if (hasAnyInstitutionRole(user, item.requireInstitutionRole)) {
      return true;
    }
  }

  // Check minimum user role requirement
  if (item.requireAtLeastRole) {
    return isAtLeastRole(user.user_role, item.requireAtLeastRole);
  }

  // If no conditions are met, deny access
  return false;
}

/**
 * Filters a list of navigation items based on user roles and additional predicates.
 *
 * @param items - The original list of navigation items to filter
 * @param user - The current user, or null if no user is logged in
 * @param predicates - Additional filtering functions to apply to each navigation item
 * @returns {NavItem[]} - Filtered list of navigation items
 *
 * @description
 * This function performs a recursive filtering of navigation items:
 * 1. Checks if each item meets role-based access requirements
 * 2. Applies any additional custom predicates
 * 3. Recursively filters child items
 * 4. Includes parent items if they have accessible children, even if the parent itself is filtered out
 */
export function filterInstitutionNavItems(
  items: NavItem[],
  user: MagicSchoolUser | null,
  predicates: ((item: NavItem) => boolean)[],
): NavItem[] {
  return items.filter((item) => {
    // Check role requirements
    const hasRequiredRole = canUserRoleAccessItem(item, user);

    // Check other predicates
    const passesPredicates = predicates.every((predicate) => predicate(item));
    const isItemValid = hasRequiredRole && passesPredicates;

    // Recursively filter child items
    if (item.children && item.children.length > 0) {
      item.children = filterInstitutionNavItems(item.children, user, predicates);
    }

    // Include the item if:
    // 1. It passes all role and predicate checks, OR
    // 2. It has accessible children
    return isItemValid || (item.children && item.children.length > 0);
  });
}

export function createNavFilter(user: MagicSchoolUser, predicates: ((item: NavItem) => boolean)[]) {
  return (items: NavItem[]) =>
    user.institutionRoles?.length
      ? filterInstitutionNavItems(items, user, predicates)
      : filterNavItems(items, [
          (item) => !item.requireAtLeastRole || isAtLeastRole(user.user_role as UserRole, item.requireAtLeastRole),
          ...predicates,
        ]);
}

/**
 * Determines if a navigation item is active based on the current URL path, or if any of its children are active.
 */
export const isNavItemActive = (item: NavItem, pathname?: string): boolean =>
  Boolean(
    (item.url && (pathname === item.url || pathname === item.altUrl || (!item.exactMatch && pathname?.startsWith(item.url)))) ||
      item.matchPathPrefixes?.some((prefix) => pathname?.startsWith(prefix)) ||
      item?.children?.some((child) => isNavItemActive(child, pathname)),
  );

/**
 * Determines if a navigation item is the current active item based on the URL path.
 */
export const isNavItemCurrent = (item: NavItem, pathname?: string): boolean =>
  Boolean(item.url && (pathname === item.url || pathname === item.altUrl));
